import React from 'react';
import { Box, Typography } from '@mui/material';
import BaseCard from './BaseCard';
import HubIcon from '../../../images/icons/HubIcon';
import {
  customError,
  dimGrey,
  errorBg,
  lightBackground1,
  lightBlack,
  lightGrey,
  lightPurple,
} from '../../../mainTheme';

interface HubCardProps {
  isSelected?: boolean;
  onClick?: () => void;
  data: {
    offline: number;
    unassigned: number;
    online: number;
    total: number;
  };
}

const StatusBox: React.FC<{
  count: number;
  status: string;
  isSelected: boolean;
}> = ({ count, status, isSelected }) => {
  const getColor = (status: string) => {
    switch (status) {
      case 'offline':
        return {
          bg: errorBg,
          border: `2px solid var(--Semantics-S10---Error-Ic-F, ${customError})`,
        };
      case 'unassigned':
        return {
          bg: 'var(--Semantics-S11---Warning-Bg, #FFF4E5)',
          border: '2px solid var(--Semantics-S10---Warning-Ic-F, #FFA726)',
        };
      case 'online':
        return {
          bg: 'var(--Semantics-S11---Success-Bg, #E8F5E9)',
          border: '2px solid var(--Semantics-S10---Success-Ic-F, #66BB6A)',
        };
      default:
        return {
          bg: lightBackground1,
          border: '5px solid transparent',
        };
    }
  };

  const { bg, border } = getColor(status);

  return (
    <Box
      sx={{
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mb: ' 20px',
      }}
    >
      <Typography
        sx={{
          width: 56,
          height: 56,
          flexShrink: 0,
          borderRadius: '50%',
          bgcolor: bg,
          color: isSelected ? lightBlack : dimGrey,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: '8px',
          border: border,
          fontSize: '22px',
          fontWeight: isSelected ? 700 : 500,
        }}
      >
        {count}
      </Typography>
      <Typography
        sx={{
          color: dimGrey,
          fontSize: '12px',
          fontWeight: 500,
          lineHeight: '16px',
        }}
      >
        {status}
      </Typography>
    </Box>
  );
};

const HubCard: React.FC<HubCardProps> = ({ isSelected, onClick, data }) => (
  <BaseCard
    title="Hubs"
    isSelected={isSelected}
    onClick={onClick}
    totalCount={`${data.total} total`}
    icon={
      <HubIcon
        color={isSelected ? '#D5C6DC' : lightGrey}
        secondaryColor={isSelected ? lightPurple : dimGrey}
      />
    }
  >
    <Box
      sx={{
        display: 'flex',
        gap: '28px',
        justifyContent: 'flex-end',
      }}
    >
      {['offline', 'unassigned', 'online'].map((status) => (
        <StatusBox
          key={status}
          count={data[status as keyof typeof data]}
          status={status}
          isSelected={isSelected || false}
        />
      ))}
    </Box>
  </BaseCard>
);

export default HubCard;
